import { Select, message } from "antd";
import { useState } from "react";
import { useMutation } from "react-query";
import { IndicatorInfo, RequiredIndicator } from "../../components";
import { nextOfKinRelationships, OCCUPATIONS } from "../../helpers/config";
import _ from "lodash";
import { Stepper } from "@mantine/core";
import { postPublicMember } from "../../helpers/api";

const relativeTemplate = {
  firstName: "",
  lastName: "",
  primaryContact: "",
  secondaryContact: "",
  relationship: "",
  gender: "",
};

const memberTemplate = {
  firstName: "",
  lastName: "",
  gender: "",
  dob: "",
  contact: "",
  secondaryContact: "",
  occupation: "",
  email: "",
  location: "",
};

const NewMemberForm = ({
  onSuccess,
  onError,
  onExit,
  showCharm = true,
  showBottomButtons = false,
  facilityId = null,
}) => {
  const [member, setMember] = useState(memberTemplate);
  const [relative, setRelative] = useState(relativeTemplate);

  const [active, setActive] = useState(0);
  const maxSteps = 1;
  const successStep = 2;

  // handlers
  const nextStep = () =>
    setActive((current) => (current < maxSteps ? current + 1 : current));
  const prevStep = () =>
    setActive((current) => (current > 0 ? current - 1 : current));

  const { Option } = Select;

  const { mutateAsync: createMember, isLoading } = useMutation(
    (data) => postPublicMember({ ...data, facilityId }),
    {
      onSuccess: (data, variables, context) => {
        if (data.status === 201) {
          message.success(data.data.message);
          onSuccess();
          setMember(memberTemplate);
          setRelative(relativeTemplate);
          setActive(0);
          return;
        }
        throw data;
      },
      onError: (error, variables, context) => {
        const err = error?.response?.data?.message;
        if (_.isArray(err)) {
          err.map((err) => message.error(err.message));
        } else {
          message.error(err || "error processing request!");
        }
      },
    }
  );

  return (
    <div className="px-2">
      <div>
        <IndicatorInfo />
        <Stepper active={active} onStepClick={setActive} className="mt-5">
          <Stepper.Step label="Member" key="step-member" description="bio info">
            <div className="row">
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="firstName">
                  First Name
                  <RequiredIndicator />
                </label>
                <input
                  className="input"
                  type="text"
                  autoFocus
                  value={member.firstName}
                  onChange={(e) =>
                    setMember({ ...member, firstName: e.target.value })
                  }
                  id="firstName"
                  key="firstName"
                  placeholder="e.g. John"
                />
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="lastName">
                  Last Name
                  <RequiredIndicator />
                </label>
                <input
                  className="input"
                  type="text"
                  value={member.lastName}
                  onChange={(e) =>
                    setMember({ ...member, lastName: e.target.value })
                  }
                  id="lastName"
                  key="lastName"
                  placeholder="e.g. Joe"
                />
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="gender">
                  Gender
                  <RequiredIndicator />
                </label>
                <Select
                  required
                  allowClear
                  showSearch
                  onChange={(v) => setMember({ ...member, gender: v })}
                  value={member.gender}
                  size="large"
                  id="gender"
                  key="gender"
                  className="d-block"
                  placeholder="gender">
                  <Option value="Male">Male</Option>
                  <Option value="Female">Female</Option>
                </Select>
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="dob">
                  Date of Birth
                  <RequiredIndicator />
                </label>
                <input
                  className="input"
                  type="date"
                  value={member.dob}
                  onChange={(e) =>
                    setMember({ ...member, dob: e.target.value })
                  }
                  id="dob"
                  key="dob"
                  placeholder="date of birth"
                />
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="contact">
                  Primary Contact
                  <RequiredIndicator />
                </label>
                <input
                  className="input"
                  type="tel"
                  value={member.contact}
                  onChange={(e) =>
                    setMember({ ...member, contact: e.target.value })
                  }
                  id="contact"
                  key="contact"
                  placeholder="0501234567"
                />
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="secondaryContact">
                  Secondary Contact
                </label>
                <input
                  className="input"
                  type="tel"
                  value={member.secondaryContact}
                  onChange={(e) =>
                    setMember({ ...member, secondaryContact: e.target.value })
                  }
                  id="secondaryContact"
                  key="secondaryContact"
                  placeholder="0508901234"
                />
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="occupation">
                  Occupation
                </label>
                <Select
                  allowClear
                  showSearch
                  onChange={(v) => setMember({ ...member, occupation: v })}
                  value={member.occupation || undefined}
                  size="large"
                  id="occupation"
                  key="occupation"
                  className="d-block"
                  placeholder="Select occupation">
                  {OCCUPATIONS}
                </Select>
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="email">
                  Email
                </label>
                <input
                  className="input"
                  type="email"
                  value={member.email}
                  onChange={(e) =>
                    setMember({ ...member, email: e.target.value })
                  }
                  id="email"
                  key="email"
                  placeholder="me@email.com"
                />
              </div>
              <div className="field col-12">
                <label className="mb-0" htmlFor="location">
                  Location
                </label>
                <input
                  className="input"
                  type="text"
                  value={member.location}
                  onChange={(e) =>
                    setMember({ ...member, location: e.target.value })
                  }
                  id="location"
                  key="location"
                  placeholder="e.g. Madina"
                />
              </div>
            </div>
          </Stepper.Step>
          <Stepper.Step
            onStepClick={setActive}
            key="step-relative"
            label="Next of Kin's"
            description="details">
            <div className="row mt-3 mb-5">
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="kinFirstName">
                  First Name
                </label>
                <input
                  className="input"
                  type="text"
                  value={relative.firstName}
                  onChange={(e) =>
                    setRelative({ ...relative, firstName: e.target.value })
                  }
                  id="kinFirstName"
                  key="kinFirstName"
                  placeholder="e.g. Mary"
                />
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="kinLastName">
                  Last Name
                </label>
                <input
                  className="input"
                  type="text"
                  id="kinLastName"
                  key="kinLastName"
                  value={relative.lastName}
                  onChange={(e) =>
                    setRelative({ ...relative, lastName: e.target.value })
                  }
                  placeholder="e.g. Dartey"
                />
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="kinGender">
                  Gender
                </label>
                <Select
                  required
                  value={relative.gender}
                  onChange={(v) => setRelative({ ...relative, gender: v })}
                  allowClear
                  showSearch
                  size="large"
                  id="kinGender"
                  key="kinGender"
                  className="d-block"
                  placeholder="gender">
                  <Option value="Male">Male</Option>
                  <Option value="Female">Female</Option>
                </Select>
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="relationship">
                  Relationship
                </label>
                <Select
                  required
                  allowClear
                  showSearch
                  value={relative.relationship}
                  onChange={(v) =>
                    setRelative({ ...relative, relationship: v })
                  }
                  size="large"
                  key="relationship"
                  id="relationship"
                  className="d-block"
                  placeholder="gender">
                  {nextOfKinRelationships}
                </Select>
              </div>
              <div className="field col-md-6 col-12">
                <label className="mb-0" htmlFor="kinPrimaryContact">
                  Primary Contact
                </label>
                <input
                  className="input"
                  type="tel"
                  id="kinPrimaryContact"
                  key="kinPrimaryContact"
                  value={relative.primaryContact}
                  onChange={(e) =>
                    setRelative({ ...relative, primaryContact: e.target.value })
                  }
                  placeholder="0301234567"
                />
              </div>
              <div className="field col-md-6 col-12 mb-5">
                <label className="mb-0" htmlFor="kinSecondaryContact">
                  Secondary Contact
                </label>
                <input
                  className="input"
                  value={relative.secondaryContact}
                  onChange={(e) =>
                    setRelative({
                      ...relative,
                      secondaryContact: e.target.value,
                    })
                  }
                  type="tel"
                  id="kinSecondaryContact"
                  key="kinSecondaryContact"
                  placeholder="0301234567"
                />
              </div>
            </div>
          </Stepper.Step>
        </Stepper>
      </div>
      <div className="text-center my-4">
        <button onClick={prevStep} className="button me-2">
          <span className="bi bi-arrow-left me-2" />
          Back
        </button>
        {active !== maxSteps && (
          <button onClick={nextStep} className="button">
            Next
            <span className="bi bi-arrow-right ms-2" />
          </button>
        )}

        {active === maxSteps && (
          <button
            onClick={() => {
              const hasKin =
                relative.firstName?.trim() ||
                relative.lastName?.trim() ||
                relative.primaryContact?.trim();
              const payload = {
                ...member,
                ...(hasKin ? { relations: [relative] } : {}),
              };
              createMember(payload);
            }}
            className={`button is-info ${isLoading && " is-loading"}`}>
            <span className="bi bi-check-all me-2" />
            Submit
          </button>
        )}
      </div>
    </div>
  );
};

export default NewMemberForm;
